import { buildApiUrl } from "@/core/api/api-methods";
import { getAcceptLanguage, getApiKeyHeader } from "@/core/api/client";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useSessionStore } from "@/core/stores/session.store";
import { cn } from "@/core/utils/func/cn";
import { getDeviceHeaders } from "@/core/utils/func/deviceHeaders";
import { notifSilence, notifSilenceWarning } from "@/core/utils/func/notifApp";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

const PRECONDITIONS_ENDPOINT = "/transaction/preconditions";
const AFTER_LOADING_PRECONDITIONS = 500;
const NOT_AUTHORIZED_ROUTE = "/not-authorized";

/**
 * Version inline minimale de getTransactionPreconditions (web-app) : garantit
 * que `statusServer` est toujours le statut HTTP réel de la réponse.
 */
const getTransactionPreconditions = async (
  typeTransaction: string
): Promise<{ statusServer: number }> => {
  const token = useSessionStore.getState().accessToken;
  if (!token) {
    return { statusServer: 401 };
  }

  const queryString = new URLSearchParams({ typeTransaction }).toString();
  const fullUrl = `${buildApiUrl(PRECONDITIONS_ENDPOINT)}?${queryString}`;

  try {
    const response = await fetch(fullUrl, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "Accept-Language": getAcceptLanguage(),
        ...getApiKeyHeader(),
        ...getDeviceHeaders(),
      },
      method: "GET",
    });
    return { statusServer: Number(response.status) };
  } catch (error) {
    console.error("Error fetching preconditions:", error);
    return { statusServer: 500 };
  }
};

const TabsContext = React.createContext<{ value?: string } | null>(null);

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ value, ...props }, ref) => {
  return (
    <TabsContext.Provider value={{ value }}>
      <TabsPrimitive.Root ref={ref} value={value} {...props} />
    </TabsContext.Provider>
  );
});
Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex justify-center items-center p-1 h-10 rounded-2xl bg-muted text-muted-foreground",
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-2xl px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-white data-[state=active]:bg-brand-700 data-[state=active]:shadow-sm",
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

interface TabsContentWithPreconditionsProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {
  preconditions?: string;
  children: React.ReactNode;
}

const TabsContentWithPreconditions = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  TabsContentWithPreconditionsProps
>(({ className, preconditions, children, value, ...props }, ref) => {
  const navigate = useNavigate();
  const { t } = useTypedTranslation();
  const tabsContext = React.useContext(TabsContext);
  const isActiveTab = React.useMemo(
    () => tabsContext?.value === value,
    [tabsContext?.value, value]
  );
  const hasPreconditions = Boolean(
    preconditions && preconditions !== undefined && preconditions !== ""
  );
  const [isCheckingPreconditions, setIsCheckingPreconditions] = React.useState(
    hasPreconditions && isActiveTab
  );
  const [isAuthorized, setIsAuthorized] = React.useState<boolean | null>(
    hasPreconditions && isActiveTab ? null : true
  );
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const redirectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Mettre à jour l'état quand l'onglet devient actif/inactif
  React.useEffect(() => {
    if (isActiveTab && hasPreconditions && isAuthorized === null) {
      // L'onglet est devenu actif et les préconditions doivent être vérifiées
      setIsCheckingPreconditions(true);
    } else if (!isActiveTab) {
      // L'onglet est devenu inactif, réinitialiser l'état
      setIsCheckingPreconditions(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    }
  }, [isActiveTab, hasPreconditions, isAuthorized]);

  React.useEffect(() => {
    const checkIsActive = () => tabsContext?.value === value;

    // Nettoyer les timeouts précédents si l'onglet devient inactif
    if (!checkIsActive()) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
      setIsCheckingPreconditions(false);
      return;
    }

    if (preconditions && preconditions !== undefined && preconditions !== "") {
      setIsCheckingPreconditions(true);
      setIsAuthorized(null);
      timeoutRef.current = setTimeout(() => {
        getTransactionPreconditions(preconditions)
          .then((res) => {
            // Vérifier que l'onglet est toujours actif avant de traiter le résultat
            if (!checkIsActive()) return;

            setIsCheckingPreconditions(false);
            // Le statusServer est toujours le statut HTTP de la réponse (200, 401, etc.)
            // Il est garanti d'être un nombre grâce à la fonction getTransactionPreconditions
            const statusServer = Number(res?.statusServer);
            if (statusServer === 200) {
              notifSilence("accessAuthorized", 5000);
              setIsAuthorized(true);
            } else {
              notifSilenceWarning("restrictContent", 5000);
              setIsAuthorized(false);
              // Vérifier à nouveau que l'onglet est actif avant de rediriger
              redirectTimeoutRef.current = setTimeout(() => {
                if (checkIsActive()) {
                  navigate(NOT_AUTHORIZED_ROUTE);
                }
              }, AFTER_LOADING_PRECONDITIONS);
            }
          })
          .catch((error) => {
            // Vérifier que l'onglet est toujours actif avant de traiter l'erreur
            if (!checkIsActive()) return;

            console.error("Error fetching preconditions:", error);
            setIsCheckingPreconditions(false);
            setIsAuthorized(false);
            // Vérifier à nouveau que l'onglet est actif avant de rediriger
            redirectTimeoutRef.current = setTimeout(() => {
              if (checkIsActive()) {
                navigate(NOT_AUTHORIZED_ROUTE);
              }
            }, AFTER_LOADING_PRECONDITIONS);
          });
      }, 5000);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
          redirectTimeoutRef.current = null;
        }
        setIsCheckingPreconditions(false);
      };
    } else {
      setIsCheckingPreconditions(false);
      setIsAuthorized(true);
    }
  }, [preconditions, navigate, value, tabsContext]);

  if (isAuthorized === false) {
    return (
      <TabsPrimitive.Content
        ref={ref}
        value={value}
        className={cn(
          "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          className
        )}
        {...props}
      >
        <div className="flex justify-center items-center px-4 py-8 text-sm text-destructive">
          {t("noAccessPermission")}
        </div>
      </TabsPrimitive.Content>
    );
  }

  return (
    <TabsPrimitive.Content
      ref={ref}
      value={value}
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "relative",
        className
      )}
      {...props}
    >
      <div
        className={cn(
          "transition-all duration-300",
          isCheckingPreconditions && "blur-sm pointer-events-none"
        )}
      >
        {children}
      </div>
      {isCheckingPreconditions && (
        <div
          className={cn(
            "absolute inset-0 z-10",
            "flex gap-2 justify-center items-center",
            "bg-transparent",
            "pointer-events-none"
          )}
        >
          <div
            className={cn(
              "flex gap-2 justify-center items-center px-4 py-2.5",
              "bg-white dark:bg-zinc-800",
              "border border-zinc-200 dark:border-zinc-700",
              "rounded-lg shadow-xl",
              "backdrop-blur-sm"
            )}
          >
            <Loader2 className="w-4 h-4 text-destructive animate-spin" />
            <span className="text-sm font-medium whitespace-nowrap text-zinc-700 dark:text-zinc-200">
              {t("checkingPreconditions")}
            </span>
          </div>
        </div>
      )}
    </TabsPrimitive.Content>
  );
});
TabsContentWithPreconditions.displayName = "TabsContentWithPreconditions";

export {
  Tabs,
  TabsContent,
  TabsContentWithPreconditions,
  TabsList,
  TabsTrigger,
};
